import React from 'react';

import Layout from '../components/Layout';
import Sidebar from '../components/Sidebar';
import config from '../../config';
import Logo from '../components/Logos';

const skillGroups = [
  {
    title: 'AI & GenAI',
    icon: 'fa-robot',
    accent: true,
    skills: [
      'Agentic AI',
      'RAG',
      'Claude Code',
      'Cursor',
      'Ollama',
      'LangChain',
      'LangGraph',
      'Langfuse',
      'Vector DBs',
    ],
  },
  {
    title: 'Big Data Processing',
    icon: 'fa-bolt',
    skills: ['Apache Spark', 'Apache Flink', 'Apache Kafka', 'Hadoop'],
  },
  {
    title: 'Cloud Data Platforms',
    icon: 'fa-cloud',
    skills: ['Databricks', 'Snowflake', 'Azure Synapse', 'Redshift'],
  },
  {
    title: 'Cloud & On-Premise',
    icon: 'fa-server',
    skills: [
      'AWS',
      'Azure',
      'GCP',
      'Cloudera CDH',
      'Ceph',
      'Rancher Kubernetes',
      'MetalLB',
    ],
  },
  {
    title: 'Data Formats & Lakehouse',
    icon: 'fa-database',
    skills: ['Parquet', 'Delta Lake', 'Apache Iceberg', 'Avro', 'JSON', 'YAML'],
  },
  {
    title: 'Data Management & Governance',
    icon: 'fa-shield-alt',
    skills: [
      'Apache Ranger',
      'Apache Atlas',
      'Amundsen',
      'DataHub',
      'Open Metadata',
      'Great Expectations',
    ],
  },
  {
    title: 'ML-Ops',
    icon: 'fa-cogs',
    skills: ['Kubeflow', 'KServe', 'MLflow', 'Seldon', 'Feast'],
  },
  {
    title: 'Programming & IDE',
    icon: 'fa-code',
    skills: [
      'Python',
      'Pandas',
      'Polars',
      'SQL',
      'dbt',
      'SQL Mesh',
      'Unix / Shell',
      'SAS',
      'Jupyter',
      'VS Code',
      'PyCharm',
    ],
  },
  {
    title: 'Databases',
    icon: 'fa-table',
    skills: [
      'PostgreSQL',
      'MySQL',
      'Teradata',
      'DB2',
      'Informix',
      'HBase',
      'Trino',
      'Presto',
      'Druid',
      'DuckDB',
    ],
  },
  {
    title: 'Orchestration',
    icon: 'fa-stream',
    skills: ['Apache Airflow', 'Dagster', 'Apache Oozie', 'Control-M'],
  },
  {
    title: 'Observability',
    icon: 'fa-chart-line',
    skills: [
      'Grafana',
      'Prometheus',
      'Loki',
      'Thanos',
      'Elasticsearch',
      'Kibana',
      'Logstash',
      'SigNoz',
      'OpenTelemetry',
    ],
  },
  {
    title: 'CI/CD, DevOps & IaC',
    icon: 'fa-infinity',
    skills: [
      'Kubernetes',
      'Docker',
      'Helm',
      'Terraform',
      'CloudFormation',
      'Azure ARM',
      'GitHub Actions',
      'GitLab CI',
      'Argo CD',
      'Ansible',
      'Spinnaker',
      'Tekton',
    ],
  },
];

const experience = [
  {
    role: 'Director — AI & Data Engineering',
    company: 'ERNST & YOUNG (AI & DATA) | SINGAPORE',
    url: 'https://www.ey.com/',
    urlLabel: 'ey.com',
    date: 'September 2025 - Present',
    bullets: [
      'Principal AI & Data Engineer in the Technology Consulting division, leading a team of 10+ AI, Data & Software Engineers.',
      'Working with clients across ASEAN on GenAI, Agentic AI and real-time data solutions and architecture.',
      'Building EY’s Agentic AI Accelerator — the Decisions Operation Layer platform — bringing Agents to enterprise customers with end-to-end Agent observability, governance and guardrails.',
      'Delivering GenAI projects for FairPrice Group (POS AI Knowledge Assistant) and Temasek (AI Fluency Assessment Platform).',
    ],
  },
  {
    role: 'Head of Data Engineering & Architecture',
    company: 'CHUBB (CHUBB LIFE) | SINGAPORE',
    url: 'https://www.chubb.com/sg-en/',
    urlLabel: 'chubb.com',
    date: 'June 2025 - September 2025',
    bullets: [
      'Partnered with global senior leadership to define and execute Chubb Life’s enterprise-wide Data & AI strategy, shaping technology roadmaps to drive measurable business impact.',
      'Built, coached and scaled a high-performing regional team of 50+ Engineers, establishing a COE for Data Engineering and applied AI.',
      'Directed the implementation of a cloud-native data platform with scalable frameworks for ingestion, transformation and governance.',
      'Led development of applied ML solutions, including RAG-based AI assistants for self-service analytics, and pioneered AI Agents to automate and scale DE and ML.',
    ],
  },
  {
    role: 'Data Engineering Manager / Staff Data Engineer',
    company: 'TEMUS (AI & DATA) | SINGAPORE',
    url: 'https://temus.com/',
    urlLabel: 'temus.com',
    date: 'April 2024 - June 2025',
    bullets: [
      'Managed, mentored and coached a team of 20+ Data Engineers covering Data Engineering, MLOps, AIOps, and Data & AI Architecture.',
      'Data architecture lead for Public and Private sector clients — standardized architectural frameworks and methodologies for scalable, reusable solutions.',
      'Delivered the Industry Partnerships Platform for Ngee Ann Polytechnic using LLMs for conversational AI on Azure GCC.',
      'Lead Engineer & Architect for FWD Insurance — rolled out LLM-powered voice-to-voice conversational AI avatars for sales agent training, scaled on EKS to serve thousands of users across South East Asia.',
      'Worked with Temasek Trust & Laconic on Lakehouse design on Azure & AWS using Synapse, EKS, ADF, Spark, dbt and Open Metadata.',
      'Led pre-sales, business development, proposals and tech consulting; closed deals worth 500k SGD.',
      'Developed internal solutions to automate ETL development using LLMs and data governance using Great Expectations & Open Metadata.',
    ],
  },
  {
    role: 'Data Engineering Manager / Staff Data Engineer',
    company: 'GLOBAL FASHION GROUP (GSF DATA) | SINGAPORE',
    url: 'https://global-fashion-group.com/',
    urlLabel: 'global-fashion-group.com',
    date: 'November 2021 - March 2024',
    bullets: [
      'Worked with Dafiti (LATAM) on data strategy, restructuring, prioritisation, roadmap planning and adopting data mesh architecture by treating data as a product.',
      'Managed a team of 8 Data & MLOps Engineers across Vietnam and India covering Data Engineering, DevOps, MLOps, Solution Architecture, Data Governance, Observability and Cloud.',
      'Staff Engineer / Architect for the Global Data Platform (GDP) built on OSS CNCF, LF AI & ASF stack on Kubernetes (AWS EKS) — the central data platform for GFG, Zalora, The Iconic and Dafiti, including Model Serving.',
      'Technical leader for GDP (Infrastructure as a Service) and DaaS (Data as a Service); delivered data and ML solutions for Pricing Engine, Recommendation and Personalisation with Zalora (SEA) and The Iconic (ANZ).',
      'Led the shutdown of Snowflake and Redshift cloud DWHs to cut cost, migrating to a Trino-backed Iceberg Lakehouse.',
      'Helped Zalora, The Iconic and Dafiti migrate Seller Dashboards to GDP for a subscription-based data product.',
      'Ingested GA3 (The Iconic, Dafiti) and Segment (Zalora) events into GDP for behavioural data recommendation.',
      'Improved observability and availability using Loki, Thanos, Grafana and Prometheus with Slack and email alerts.',
      'Incorporated GitOps methodology; set up CI/CD with GitHub Actions, Argo CD, Ansible and Terraform.',
      'Set up Data Governance & Quality for the Iceberg Lakehouse using Apache Ranger, DataHub and Great Expectations.',
      'Set up the ML-Ops stack: MLflow (experimentation), Feast (feature store), KServe (model serving), Kong (API gateway).',
    ],
  },
  {
    role: 'Data Engineering Manager / Lead Data Engineer',
    company: 'SINGTEL / TRUSTWAVE (EMERGING TECH) | SINGAPORE',
    url: 'https://www.singtel.com/',
    urlLabel: 'singtel.com',
    date: 'August 2020 - November 2021',
    bullets: [
      'Built the Telco Cyber Data Lake & Platform using Hybrid Cloud for Cyber Analytics and Threat Intelligence for Singtel Cybersecurity.',
      'Lead Architect and Engineer for the Telco CDL Kubernetes Platform — on-premise with Rancher and on AWS with EKS.',
      'Processed TBs of data using Spark on K8s stored in Kafka, Ceph and S3 across AWS and on-premise.',
      'Built streaming and batch pipelines with Apache Kafka and Spark Streaming, orchestrated with Airflow.',
      'Set up logging and monitoring using Elasticsearch, FluentD, Grafana and Prometheus.',
      'Set up DevOps and CI/CD processes using GitLab CI, Argo CD, Ansible and Terraform.',
      'Set up Data Governance for Kafka, Ceph, Hive and S3 using Apache Atlas, Apache Ranger and Amundsen.',
      'Set up ML-Ops with MLflow to track experiments, metrics and parameters.',
    ],
  },
  {
    role: 'Data Science Lead',
    company: 'GRAB (ML & AI INFRASTRUCTURE) | SINGAPORE',
    url: 'https://www.grab.com/',
    urlLabel: 'grab.com',
    date: 'September 2018 - July 2020',
    bullets: [
      'Technical lead for the ML Infrastructure team — cloud architecture across AWS and Azure for Data Science, spanning ML-Ops, DevOps, Cloud, Data Engineering and Data Science.',
      'Led a team of ML Engineers building and supporting ML Platforms on AWS and Azure — data engineering, feature engineering, model training and deployment.',
      'Data Science PIC for vendor engagement with AWS, Microsoft and Databricks; owner of all DS cloud accounts.',
      'Implemented Data and Infrastructure Governance across ML Platforms on AWS and Azure.',
      'Developed the GrabFood restaurant behavioural recommendation system with the Azure CAT team, increasing click-through rate by 170% over existing recommenders.',
      'Developed a multi-user JupyterHub and Kubeflow Data Science ML Platform on AWS EKS for ML and analytics.',
      'Automated cloud infrastructure with Terraform and Azure ARM; used Helm on EKS and AKS.',
      'Architected platform tooling: Spark UI Proxy, JupyterHub resource tracking, AWS and Datadog monitoring for EKS, and Agartha — Docker build automation via Git pull requests.',
    ],
  },
  {
    role: 'Senior Data Engineer',
    company: 'ROYAL BANK OF SCOTLAND (ENTERPRISE SOLUTIONS) | GURUGRAM, INDIA',
    url: 'https://www.rbs.com/',
    urlLabel: 'rbs.com',
    date: 'July 2017 - August 2018',
    bullets: [
      'Part of Cloud Engineering, responsible for migrating the Athena SAS DWH onto AWS for Risk & Finance Solutions.',
      'Designed the Data Lake on AWS used by Data Engineering & Analytics for ETL, analytics and machine learning.',
      'Automated AWS cloud infrastructure using Terraform.',
      'Built a Python parser to auto-convert HiveQL to PySpark (Spark SQL) jobs on AWS EMR, reducing conversion time by over 90%.',
      'Reduced job workflow creation time by 80% with an automated Oozie workflow creation framework.',
      'Built monitoring for active EMR clusters and SageMaker instances across regions using Boto3 on Lambda, with SES notifications scheduled via CloudWatch.',
      'Enabled Amazon SageMaker for Risk Modelling with Spark, locally and via remote EMR clusters; persisted notebooks in S3.',
      'Installed and set up Apache Airflow to schedule core Spark ETL workflows.',
    ],
  },
  {
    role: 'Senior Data Engineer',
    company: 'EXL (BARCLAYS DECISION ANALYTICS) | GURUGRAM, INDIA',
    url: 'https://www.exlservice.com/',
    urlLabel: 'exlservice.com',
    date: 'December 2016 - July 2017',
    bullets: [
      'Migrated the SAS Datawarehouse onto Cloudera for the Risk Solutions vertical of Barclays.',
      'Designed an on-premise data lake using Cloudera CDH for Data Engineering & Analytics ETL workloads.',
      'Developed real-time and batch data pipelines using Spark Streaming, PySpark and HBase.',
    ],
  },
  {
    role: 'Senior Data Engineer',
    company: 'AON (BUSINESS INTELLIGENCE) | GURUGRAM, INDIA',
    url: 'https://www.aon.com/',
    urlLabel: 'aon.com',
    date: 'November 2013 - December 2016',
    bullets: [
      'Delivered end-to-end Big Data projects: data ingestion, ETL, data lake design, data migration, unit test plans and POCs.',
      'Designed and developed the HRBPO Attendance Tracking System using Hive and Hadoop.',
      'Ran POCs across the Hadoop ecosystem: HDFS Snapshots, HA for HDFS/Hive/YARN/HUE, HBase, Active Directory integration, Hive on Spark, Scala on Spark.',
      'Owned production ETL monitoring via Control-M, plus fixes and enhancements in Pig Latin, HiveQL, Unix and SQL.',
    ],
  },
];

const projectGroups = [
  {
    title: 'Data, ML & AI Engineering',
    projects: [
      {
        name: 'Chubb, Singapore',
        description:
          'Led Data Engineering for business-critical dashboards (Daily Sales, Agency Metrics, Customer 360). Designed digital consumption of Customer 360 data via Mobile, Web and CRM.',
      },
      {
        name: 'Temus, Singapore',
        description:
          'Led Data and AI Engineering on AWS and Azure for Public and Private sector clients across optimisation, digitalisation and migration use cases.',
      },
      {
        name: 'Global Fashion Group, Singapore',
        description:
          'Led the Data Engineering track supporting ML use cases around recommendation and personalisation. Supported Zalora’s pricing engine, Data as a Service and Datajet (ML as a Service).',
      },
    ],
  },
  {
    title: 'Data & ML Platforms',
    projects: [
      {
        name: 'Global Data Platform — GFG',
        description:
          'Led development of GDP on AWS EKS: end-to-end Data & ML infrastructure for GFG, Zalora and The Iconic — ingestion from varied sources for recommendations, search, personalisation and inventory replenishment.',
      },
      {
        name: 'Telco Cyber Data Lake — Trustwave',
        description:
          'Led development of the Telco CDL platform on hybrid Kubernetes (Rancher on-premise, AWS EKS in cloud). Processed TBs of Singtel network logs for Threat Intelligence and Analytics.',
      },
      {
        name: 'AWS & Azure ML Platforms — Grab',
        description:
          'Led development of ML Platforms on AWS EKS and Azure Databricks powering production ML for Grab Transport and GrabFood — recommender systems, surge pricing, order batching and personalisation.',
      },
    ],
  },
  {
    title: 'Data & ETL Migrations',
    projects: [
      {
        name: 'Athena Re-platforming — RBS (SAS DWH to AWS)',
        description:
          'Migrated 2000+ ETL scripts from SAS to PySpark on AWS EMR, Glue and Athena.',
      },
      {
        name: 'Barclays Bureau — EXL (SAS DWH to Cloudera)',
        description:
          'Migrated 1000+ ETL scripts from SAS to PySpark on Cloudera’s Hadoop Platform.',
      },
      {
        name: 'Informix Shutdown — AON (Legacy DWH to Cloudera)',
        description:
          'Migrated 2000+ ETL scripts from Unix/SQL to Pig Latin on Cloudera’s Hadoop Platform.',
      },
    ],
  },
];

const IndexPage = () => (
  <Layout>
    <Sidebar />
    <div className="container-fluid p-0">
      <section
        className="resume-section p-3 p-lg-5 d-flex align-items-center"
        id="about"
      >
        <div className="w-100">
          <div className="hero-role">
            Director — AI &amp; Data Engineering · EY, Singapore
          </div>
          <h1 className="mb-3">
            {config.firstName} <span className="text-primary">{config.lastName}</span>
          </h1>
          <div className="hero-contact mb-4">
            {config.address}
            <span className="dot">·</span>
            {config.phone}
            <span className="dot">·</span>
            <a href={`mailto:${config.email}`}>{config.email}</a>
          </div>
          <p className="lead mb-5">
            Data &amp; AI Engineering leader with 14 years of experience
            architecting enterprise-scale Data &amp; AI solutions. Currently
            Director of AI &amp; Data Engineering at Ernst &amp; Young,
            Singapore, leading AI, Data &amp; Software Engineers in the
            Technology Consulting division. I work with clients across ASEAN
            on GenAI, Agentic AI and real-time data platforms — building
            EY&apos;s Agentic AI Accelerator — the Decisions Operation Layer
            platform — with end-to-end
            agent observability, governance and guardrails, and delivering
            LLM-powered solutions in production: RAG-based AI assistants,
            Text2SQL analytics, and voice-to-voice conversational AI avatars
            serving thousands of users. Proven track record leading
            high-performing teams and driving digital transformation with
            significant cost savings and efficiency gains. Expert in Data,
            AI &amp; ML Engineering, Data Management &amp; Governance,
            Cloud-Native Architectures and DevOps.
          </p>
          <div className="social-icons">
            {config.socialLinks.map(social => {
              const { icon, url } = social;
              return (
                <a key={url} href={url}>
                  <i className={`fab ${icon}`}></i>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <hr className="m-0" />

      <section
        className="resume-section p-3 p-lg-5 d-flex justify-content-center"
        id="skills"
      >
        <div className="w-100">
          <h2 className="mb-5">Skills</h2>

          <div className="skill-group-title mb-3">
            <i className="fas fa-layer-group"></i>
            Technologies
          </div>
          <Logo />

          {skillGroups.map(group => (
            <div className="skill-group" key={group.title}>
              <div className="skill-group-title">
                <i className={`fas ${group.icon}`}></i>
                {group.title}
              </div>
              <ul className="skill-chips">
                {group.skills.map(skill => (
                  <li
                    className={`skill-chip${
                      group.accent ? ' skill-chip--accent' : ''
                    }`}
                    key={skill}
                  >
                    {skill}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <hr className="m-0" />

      <section
        className="resume-section p-3 p-lg-5 d-flex justify-content-center"
        id="experience"
      >
        <div className="w-100">
          <h2 className="mb-5">Experience</h2>
          {experience.map(job => (
            <div
              className="resume-item d-flex flex-column flex-md-row justify-content-between mb-5"
              key={`${job.company}-${job.date}`}
            >
              <div className="resume-content">
                <h3 className="mb-0">{job.role}</h3>
                <div className="d-inline-flex">
                  <div className="subheading mb-3 mr-3">{job.company}</div>
                  <a style={{ lineHeight: '28px' }} href={job.url}>
                    {job.urlLabel}
                  </a>
                </div>
                <ul>
                  {job.bullets.map(bullet => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              </div>
              <div className="resume-date text-md-right">
                <span className="text-primary">{job.date}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <hr className="m-0" />

      <section
        className="resume-section p-3 p-lg-5 d-flex justify-content-center"
        id="projects"
      >
        <div className="w-100">
          <h2 className="mb-5">Projects</h2>
          {projectGroups.map(group => (
            <div key={group.title} className="mb-5">
              <div className="skill-group-title mb-4">
                <i className="fas fa-layer-group"></i>
                {group.title}
              </div>
              {group.projects.map(project => (
                <div
                  className="resume-item d-flex flex-column flex-md-row justify-content-between mb-4"
                  key={project.name}
                >
                  <div className="resume-content">
                    <h3 className="mb-2">{project.name}</h3>
                    <p className="mb-0">{project.description}</p>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      <hr className="m-0" />

      <section
        className="resume-section p-3 p-lg-5 d-flex justify-content-center"
        id="education"
      >
        <div className="w-100">
          <h2 className="mb-5">Education</h2>

          <div className="resume-item d-flex flex-column flex-md-row justify-content-between mb-5">
            <div className="resume-content">
              <h3 className="mb-0">University of Pune, Maharashtra, India</h3>
              <div className="subheading mb-3">
                Bachelor of Engineering, Computer Engineering — First Class
                with Distinction
              </div>
            </div>
            <div className="resume-date text-md-right">
              <span className="text-primary">August 2009 - May 2013</span>
            </div>
          </div>

          <div className="resume-item d-flex flex-column flex-md-row justify-content-between mb-5">
            <div className="resume-content">
              <h3 className="mb-0">
                Army Public School, Dhaula Kuan, New Delhi, India
              </h3>
              <div className="subheading mb-3">PCMB Science Stream — 12th</div>
            </div>
            <div className="resume-date text-md-right">
              <span className="text-primary">April 2006 - March 2008</span>
            </div>
          </div>

          <div className="resume-item d-flex flex-column flex-md-row justify-content-between mb-5">
            <div className="resume-content">
              <h3 className="mb-0">
                Army Public School, Delhi Cantt, New Delhi, India
              </h3>
              <div className="subheading mb-3">10th</div>
            </div>
            <div className="resume-date text-md-right">
              <span className="text-primary">April 2005 - March 2006</span>
            </div>
          </div>
        </div>
      </section>

      <hr className="m-0" />

      <section
        className="resume-section p-3 p-lg-5 d-flex justify-content-center"
        id="certification"
      >
        <div className="w-100">
          <h2 className="mb-5">Certification</h2>

          <div className="resume-item d-flex flex-column flex-md-row justify-content-between mb-5">
            <div className="resume-content">
              <h3 className="mb-0">AWS Certified Developer Associate</h3>
              <div className="subheading mb-3">
                Credential ID - D1V312X1JM14QLCN
              </div>
            </div>
            <div className="resume-date text-md-right">
              <span className="text-primary">March 2018 - March 2021</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  </Layout>
);

export default IndexPage;
